package com.arena_web.pages.news.match

import androidx.compose.runtime.*
import com.arena_web.data.dto.HeadToHeadDto
import com.arena_web.data.dto.MatchDetailDto
import com.arena_web.di.LocalAppContainer
import com.arena_web.ui.components.navigation.SidebarTab
import com.arena_web.ui.layouts.AppPageLayout
import com.arena_web.ui.sections.news.MatchDetailPageContent
import com.arena_web.ui.sections.news.MatchNavSession
import com.arena_web.ui.sections.news.MatchPageContext
import com.arena_web.ui.utils.isMobile
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext

// Match detail page — lineups, timeline, stats, highlight. Reached from the
// match center list (nav session carries the clicked match) or by direct URL:
// the query string holds the football-data → TheSportsDB resolve context.
@Page("/news/match/{id}")
@Composable
fun MatchDetailPage() {
    val ctx = rememberPageContext()
    val mobile = isMobile()
    val repository = LocalAppContainer.current.newsRepository

    val matchId = ctx.route.path.substringAfterLast("/")
    val params = org.w3c.dom.url.URLSearchParams(kotlinx.browser.window.location.search)
    val seed = MatchNavSession.match?.takeIf { it.id == matchId }
    val context = remember(matchId) {
        MatchPageContext(
            sport = params.get("sport")?.takeIf { it.isNotBlank() } ?: "football",
            date = params.get("date").orEmpty().ifBlank { seed?.utcDate?.take(10).orEmpty() },
            competitionCode = params.get("comp").orEmpty().ifBlank { seed?.competitionCode.orEmpty() },
            competitionName = params.get("compName").orEmpty().ifBlank { seed?.competitionName.orEmpty() },
            homeTeam = params.get("home").orEmpty().ifBlank { seed?.homeTeam.orEmpty() },
            awayTeam = params.get("away").orEmpty().ifBlank { seed?.awayTeam.orEmpty() },
            homeCrest = params.get("homeCrest").orEmpty().ifBlank { seed?.homeCrest.orEmpty() },
            awayCrest = params.get("awayCrest").orEmpty().ifBlank { seed?.awayCrest.orEmpty() },
        )
    }

    var detail by remember(matchId) { mutableStateOf<MatchDetailDto?>(null) }
    var h2h by remember(matchId) { mutableStateOf<HeadToHeadDto?>(null) }
    var failed by remember(matchId) { mutableStateOf(false) }
    var playerId by remember { mutableStateOf<String?>(null) }

    // Head-to-head — football-data knows only its own ids, so this quietly
    // stays empty for TheSportsDB-sourced matches (basketball, UNL, …).
    LaunchedEffect(matchId) {
        if (context.sport == "football") {
            repository.getHeadToHead(context.sport, matchId).onSuccess { h2h = it }
        }
    }

    // One fetch for settled matches; live matches poll every minute so the
    // score and timeline keep ticking while the page is open.
    LaunchedEffect(matchId) {
        while (true) {
            repository.getMatchDetail(
                sport = context.sport,
                id = matchId,
                date = context.date,
                competition = context.competitionCode,
                home = context.homeTeam,
                away = context.awayTeam,
            ).onSuccess {
                detail = it
                failed = false
            }.onFailure { if (detail == null) failed = true }
            val status = detail?.status ?: seed?.status ?: ""
            if (status != "IN_PLAY" && status != "PAUSED") break
            kotlinx.coroutines.delay(60_000L)
        }
    }

    AppPageLayout(
        activeTab = SidebarTab.News,
        title = "${context.homeTeam} — ${context.awayTeam}".trim(' ', '—'),
        mobileReady = true,
        // Phones: edge-to-edge drill-in surface with a floating back button —
        // the news-article detail pattern.
        mobileHideTopBar = true,
        mobileHideBottomNav = true,
        mobileEdgeToEdge = true,
    ) {
        MatchDetailPageContent(
            context = context,
            detail = detail,
            h2h = h2h,
            failed = failed,
            mobile = mobile,
            onOpenPlayer = { playerId = it },
        )
        playerId?.let { id ->
            com.arena_web.ui.sections.news.PlayerCardOverlay(
                sport = context.sport,
                playerId = id,
                mobile = mobile,
                onDismiss = { playerId = null },
            )
        }
    }
}
